import { useEffect, useState } from 'react';
import axios from 'axios';
import { Buffer } from 'buffer';

// material ui
import { Box, Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

// graphql
import {
  CashTransferStatus,
  PageInfo,
  useCp_PlayerBalanceAdditionsQuery,
  useCp_PlayerBalanceWithdrawalsQuery,
  usePendingTransAssignCashierMutation,
} from '@/generated/graphql';

// helpers
import { formatNumber } from '@/shared/constants';

// interfaces
import {
  IConfirmTransactionInput,
  IPlayerBalanceAddition,
  IPlayerBalanceAdditionQueryVariable,
  IPlayerBalanceAdditionTable,
  IPlayerBalanceWithdrawal,
  IPlayerBalanceWithdrawalQueryVariable,
  IPlayerBalanceWithdrawalTable,
  mapGQLPlayerBalanceAddition,
  mapGQLPlayerBalanceWithdrawal,
} from '@/shared/interfaces';

// components
import { TablePlayerBalanceAdditions } from './table-addition';
import { TablePlayerBalanceWithdrawals } from './table-withdrawal';
import { ModalConfirmWithdrawalTransaction } from './confirm-withdrawal-transaction';
import { ModalConfirmAdditionTransaction } from './confirm-addition-transaction';

interface IProps {
  refetch: () => void;
}

export const PlayerTransactions = ({ refetch }: IProps) => {
  const [additions, setAdditions] = useState<IPlayerBalanceAdditionTable[]>([]);
  const [withdrawals, setWithdrawals] = useState<IPlayerBalanceWithdrawalTable[]>([]);
  const [openConfirmAdditionTransactionModal, setOpenConfirmAdditionTransactionModal] = useState<boolean>(false);
  const [openConfirmWithdrawalTransactionModal, setOpenConfirmWithdrawalTransactionModal] = useState<boolean>(false);
  const [confirmTransactinInput, setConfirmTransactinInput] = useState<IConfirmTransactionInput | undefined>();
  const [transactionPhoto, setTransactionPhoto] = useState<string>();
  const [tabValue, setTabValue] = useState('1');

  const [playerBalanceAdditionsQueryVariable, setPlayerBalanceAdditionsQueryVariable] = useState<IPlayerBalanceAdditionQueryVariable>({
    first: 25,
    filterBy: {
      status: [CashTransferStatus.Pending],
    },
  });
  const [additionPageInfo, setAdditionPageInfo] = useState<PageInfo>();
  const [additionTotalCount, setAdditionTotalCount] = useState<number>(0);

  const [playerBalanceWithdrawalsQueryVariable, setPlayerBalanceWithdrawalsQueryVariable] = useState<IPlayerBalanceWithdrawalQueryVariable>(
    {
      first: 25,
      filterBy: {
        status: [CashTransferStatus.Pending],
      },
    },
  );
  const [withdrawalPageInfo, setWithdrawalPageInfo] = useState<PageInfo>();
  const [withdrawalTotalCount, setWithdrawalTotalCount] = useState<number>(0);

  const [pendingTransAssignCashier] = usePendingTransAssignCashierMutation();

  const GetTransactionPhoto = (id: string) =>
    axios.get(
      (process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEV_BACKEND_URL : process.env.REACT_APP_PROD_BACKEND_URL) +
        '/api/v1/player-transaction/' +
        id,
      {
        responseType: 'arraybuffer',
      },
    );

  const {
    data: additionData,
    loading: additionLoading,
    error: additionError,
    refetch: additionRefetch,
  } = useCp_PlayerBalanceAdditionsQuery({
    fetchPolicy: 'network-only',
    variables: playerBalanceAdditionsQueryVariable,
    pollInterval: 5000,
  });

  const {
    data: withdrawalData,
    loading: withdrawalLoading,
    error: withdrawalError,
    refetch: withdrawalRefetch,
  } = useCp_PlayerBalanceWithdrawalsQuery({
    fetchPolicy: 'network-only',
    variables: playerBalanceWithdrawalsQueryVariable,
    pollInterval: 6500,
  });

  useEffect(() => {
    if (additionData && !additionLoading && !additionError) {
      const mapPlayerBalanceAdditions: IPlayerBalanceAddition[] = additionData.playerBalanceAdditions.nodes.map((playerBalanceAddition) =>
        mapGQLPlayerBalanceAddition(playerBalanceAddition),
      );

      const tableData = mapPlayerBalanceAdditions.map((x) => ({
        id: x.id,
        amountDisplay: formatNumber(x.amount),
        referenceId: x.referenceId,
        transactionType: x.transactionType,
        createdAt: x.createdAt,

        playerName: `${x.player.firstName} ${x.player.lastName}`,
        cashierName: `${x.cashier.firstName} ${x.cashier.lastName}`,
        auditorName: `${x.auditor.firstName} ${x.auditor.lastName}`,
        cashierId: x.cashier.id,
        paymentGatewayNumber: `${x.paymentGateway.accountNumber} (${x.paymentGateway.name})`,
        notes: x.notes,
        status: x.status,
      }));

      if (additions.length === 0 && tableData.length > 0) {
        setAdditions(tableData);
        setAdditionPageInfo(additionData.playerBalanceAdditions.pageInfo);
        setAdditionTotalCount(additionData.playerBalanceAdditions.totalCount);
      } else {
        let allStatusMatch = true;

        additions.forEach((addition) => {
          const found = tableData.find((x) => x.id === addition.id);

          if (found && (found.status !== addition.status || (found.cashierId.length > 2 && addition.cashierId.length === 0))) {
            allStatusMatch = false;
          }
        });

        if (tableData.length !== additions.length || !allStatusMatch) {
          setAdditions(() => tableData);
          setAdditionPageInfo(additionData.playerBalanceAdditions.pageInfo);
          setAdditionTotalCount(additionData.playerBalanceAdditions.totalCount);
        }
      }
    }

    if (withdrawalData && !withdrawalLoading && !withdrawalError) {
      const mapPlayerBalanceWithdrawals: IPlayerBalanceWithdrawal[] = withdrawalData.playerBalanceWithdrawals.nodes.map(
        (playerBalanceWithdrawal) => mapGQLPlayerBalanceWithdrawal(playerBalanceWithdrawal),
      );

      const tableData = mapPlayerBalanceWithdrawals.map((x) => ({
        id: x.id,
        amountDisplay: formatNumber(x.amount),
        transactionType: x.transactionType,
        createdAt: x.createdAt,
        receiverAccountNumber: x.receiverAccountNumber,

        playerName: `${x.player.firstName} ${x.player.lastName}`,
        cashierName: `${x.cashier.firstName} ${x.cashier.lastName}`,
        auditorName: `${x.auditor.firstName} ${x.auditor.lastName}`,
        cashierId: x.cashier.id,
        notes: x.notes,
        status: x.status,
      }));

      if (withdrawals.length === 0 && tableData.length > 0) {
        setWithdrawals(tableData);
      } else {
        let allStatusMatch = true;

        withdrawals.forEach((withdrawal) => {
          const found = tableData.find((x) => x.id === withdrawal.id);

          if (found && (found.status !== withdrawal.status || (found.cashierId.length > 2 && withdrawal.cashierId.length === 0))) {
            allStatusMatch = false;
          }
        });

        if (tableData.length !== withdrawals.length || !allStatusMatch) {
          setWithdrawals(() => tableData);
        }
      }
    }
  }, [additionLoading, additionError, additionData, withdrawalLoading, withdrawalError, withdrawalData]);

  const refetchAllData = () => {
    additionRefetch();
    withdrawalRefetch();
    refetch();
  };

  const transactionAction = (
    actionType: string,
    type: 'addition' | 'withdrawal',
    id: string,
    addition?: IPlayerBalanceAdditionTable,
    withdrawal?: IPlayerBalanceWithdrawalTable,
  ) => {
    switch (actionType) {
      case 'assign to me':
        pendingTransAssignCashier({
          variables: {
            input: {
              id: id,
              type: type,
            },
          },
          onCompleted() {
            refetchAllData();
          },
        });
        // Handle approve action
        break;
      case 'confirm':
        setTransactionPhoto(undefined);
        if (addition) {
          setConfirmTransactinInput({
            id: addition.id,
            notes: addition.notes,
            referenceId: addition.referenceId,
            transactionType: '',
            senderAccountNumber: addition.paymentGatewayNumber,
            receiverAccountNumber: '',

            playerName: addition.playerName,
            amountDisplay: addition.amountDisplay,
          });
        } else if (withdrawal) {
          setConfirmTransactinInput({
            id: withdrawal.id,
            notes: withdrawal.notes,
            referenceId: '',
            transactionType: withdrawal.transactionType,
            senderAccountNumber: '',
            receiverAccountNumber: withdrawal.receiverAccountNumber,

            playerName: withdrawal.playerName,
            amountDisplay: withdrawal.amountDisplay,
          });
        }

        // setSelectedTransaction(data);
        if (type === 'addition') {
          setOpenConfirmAdditionTransactionModal(true);
        } else if (type === 'withdrawal') {
          setOpenConfirmWithdrawalTransactionModal(true);
        }
        GetTransactionPhoto(id)
          .then((res) => {
            const imageBase64 = Buffer.from(res.data, 'binary').toString('base64');
            setTimeout(() => {
              setTransactionPhoto(`data:image/jpeg;base64,${imageBase64}`);
            }, 10);
          })
          .catch((err) => {
            console.error(err, 'EEEERRR');
          });
        break;
      default:
        break;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  return (
    <>
      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleTabChange} aria-label="lab API tabs example">
            <Tab label="Cash In" value="1" />
            <Tab label="Cash Out" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" style={{ padding: '0em' }}>
          <TablePlayerBalanceAdditions
            rows={additions}
            setPlayerBalanceAdditionQueryVariable={setPlayerBalanceAdditionsQueryVariable}
            playerBalanceAdditionQueryVariable={playerBalanceAdditionsQueryVariable}
            totalCount={additionTotalCount}
            pageInfo={additionPageInfo}
            getPlayerBalanceAdditionLoading={additionLoading}
            transactionAction={transactionAction}
          />
        </TabPanel>
        <TabPanel value="2" style={{ padding: '0em' }}>
          <TablePlayerBalanceWithdrawals
            rows={withdrawals}
            setPlayerBalanceWithdrawalQueryVariable={setPlayerBalanceWithdrawalsQueryVariable}
            playerBalanceWithdrawalQueryVariable={playerBalanceWithdrawalsQueryVariable}
            totalCount={withdrawalTotalCount}
            pageInfo={withdrawalPageInfo}
            getPlayerBalanceWithdrawalLoading={withdrawalLoading}
            transactionAction={transactionAction}
          />
        </TabPanel>
      </TabContext>

      <ModalConfirmAdditionTransaction
        open={openConfirmAdditionTransactionModal}
        setOpen={setOpenConfirmAdditionTransactionModal}
        data={confirmTransactinInput}
        refetch={refetchAllData}
        transactionPhoto={transactionPhoto}
      />
      <ModalConfirmWithdrawalTransaction
        open={openConfirmWithdrawalTransactionModal}
        setOpen={setOpenConfirmWithdrawalTransactionModal}
        data={confirmTransactinInput}
        refetch={refetchAllData}
      />
    </>
  );
};
